import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Medal, Trophy, Star, Flame, Target, Loader2, ChevronRight, Gift, Lock, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useBadgeNotification } from '../context/BadgeNotificationContext';

import { API } from '../config/api';

const TIER_COLORS = {
  bronze: { bg: 'from-amber-700/20 to-amber-600/20', border: 'border-amber-600/50', text: 'text-amber-500' },
  silver: { bg: 'from-gray-400/20 to-gray-300/20', border: 'border-gray-400/50', text: 'text-gray-300' },
  gold: { bg: 'from-yellow-500/20 to-yellow-400/20', border: 'border-yellow-500/50', text: 'text-yellow-400' },
  diamond: { bg: 'from-cyan-400/20 to-blue-400/20', border: 'border-cyan-400/50', text: 'text-cyan-400' }
};

const CATEGORY_ICONS = {
  consistency: Flame,
  milestone: Star,
  performance: Trophy,
  testing: Target,
  journaling: Medal,
  nutrition: Medal,
  targets: Target,
  points: Star
};

// Share badge function
const handleShareBadge = async (badge) => {
  const shareText = `🏆 ${badge.name} (${badge.tier.toUpperCase()})\n\n${badge.description}\n\n+${badge.points} points earned on BE ELITE APP!`;
  
  // Try Web Share API first (works on mobile)
  if (navigator.share) {
    try {
      await navigator.share({
        title: `${badge.name} Badge Earned!`,
        text: shareText,
        url: window.location.href
      });
      toast.success('Badge shared successfully!');
      return;
    } catch (err) {
      // User cancelled or share failed, fall back to clipboard
    }
  }
  
  // Fall back to clipboard
  try {
    await navigator.clipboard.writeText(shareText);
    toast.success('Badge details copied to clipboard!');
  } catch (err) {
    toast.error('Unable to share badge');
  }
};

export default function BadgesDisplay({ childId, childName }) {
  const [badges, setBadges] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [checking, setChecking] = useState(false);
  const { showMultipleBadges } = useBadgeNotification();

  const loadBadges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/badges/athlete/${childId}`);
      setBadges(res.data.badges || []);
      setSummary(res.data.summary);
    } catch (err) {
      console.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadBadges();
  }, [loadBadges]);

  const checkForNewBadges = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/badges/check/${childId}`);
      if (res.data.newly_earned?.length > 0) {
        // Show badge notification popup with confetti!
        showMultipleBadges(res.data.newly_earned);
        toast.success(`🎉 You earned ${res.data.newly_earned.length} new badge(s)!`);
        loadBadges();
      } else {
        toast.info('No new badges earned yet. Keep going!');
      }
    } catch (err) {
      toast.error('Failed to check badges');
    } finally {
      setChecking(false);
    }
  };

  const categories = ['all', ...new Set(badges.map(b => b.category))];
  
  const filteredBadges = selectedCategory === 'all' 
    ? badges 
    : badges.filter(b => b.category === selectedCategory);

  const earnedBadges = filteredBadges.filter(b => b.earned);
  const lockedBadges = filteredBadges.filter(b => !b.earned);

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
        <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="badges-display">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Medal className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Achievement Badges</h3>
              <p className="text-xs text-zinc-400">
                {summary?.total_earned || 0} of {summary?.total_available || 0} earned ({summary?.completion_percent || 0}%)
              </p>
            </div>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            onClick={checkForNewBadges}
            disabled={checking}
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4 mr-1" />}
            Check Badges
          </Button>
        </div>
        
        {/* Category Tabs */}
        <div className="flex gap-1 mt-3 overflow-x-auto">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-purple-500 text-white'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white'
              }`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Earned Badges */}
        {earnedBadges.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              Earned ({earnedBadges.length})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {earnedBadges.map(badge => {
                const tierStyle = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;
                const CategoryIcon = CATEGORY_ICONS[badge.category] || Medal;
                
                return (
                  <div
                    key={badge.id}
                    className={`bg-gradient-to-br ${tierStyle.bg} ${tierStyle.border} border rounded-xl p-3 transition-all hover:scale-105 group relative`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-2xl">{badge.icon}</span>
                      <div className="flex items-center gap-1">
                        <span className={`text-[10px] ${tierStyle.text} uppercase font-bold`}>{badge.tier}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareBadge(badge);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded"
                          title="Share badge"
                        >
                          <Share2 className="h-3 w-3 text-zinc-400" />
                        </button>
                      </div>
                    </div>
                    <h5 className="font-semibold text-white text-sm">{badge.name}</h5>
                    <p className="text-[10px] text-zinc-400 line-clamp-2">{badge.description}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-700/50">
                      <span className="text-xs text-purple-400">+{badge.points} pts</span>
                      <span className="text-[10px] text-zinc-500">
                        {badge.earned_at && new Date(badge.earned_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Locked Badges (In Progress) */}
        {lockedBadges.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
              <Lock className="h-4 w-4" />
              In Progress ({lockedBadges.length})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {lockedBadges.map(badge => (
                <div
                  key={badge.id}
                  className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-3 opacity-75"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-2xl grayscale">{badge.icon}</span>
                    <span className="text-[10px] text-zinc-500 uppercase">{badge.tier}</span>
                  </div>
                  <h5 className="font-semibold text-zinc-300 text-sm">{badge.name}</h5>
                  <p className="text-[10px] text-zinc-500 line-clamp-2">{badge.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="mt-2 pt-2 border-t border-zinc-700/50">
                    <div className="flex justify-between text-[10px] mb-1">
                      <span className="text-zinc-400">
                        {badge.current_value} / {badge.requirement_value}
                      </span>
                      <span className="text-[#02b5ff]">{badge.progress_percent}%</span>
                    </div>
                    <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#02b5ff] to-[#ff025f] rounded-full transition-all"
                        style={{ width: `${badge.progress_percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {filteredBadges.length === 0 && (
          <div className="text-center py-8">
            <Medal className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No badges in this category yet</p>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-800">
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-purple-400">{summary?.total_earned || 0}</div>
            <div className="text-xs text-zinc-500">Badges Earned</div>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-[#02b5ff]">{summary?.points_from_badges || 0}</div>
            <div className="text-xs text-zinc-500">Points from Badges</div>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-green-400">{summary?.completion_percent || 0}%</div>
            <div className="text-xs text-zinc-500">Complete</div>
          </div>
        </div>
      </div>
    </div>
  );
}
